/**
 * CSS Variables Generator - Creates CSS custom properties from theme tokens
 * Optional layer for features that want to use CSS custom properties in SCSS
 * 
 * Usage:
 * const cssVars = generateCSSVariables(tokens, mode);
 * 
 * Then inject into document:
 * const style = document.documentElement.style;
 * Object.entries(cssVars).forEach(([key, value]) => {
 *   style.setProperty(key, value);
 * });
 */

import {
  semantic,
} from './tokens/semantic';
import { spacing, controlHeights } from './tokens/spacing';
import { radius } from './tokens/radius';
import { shadows } from './tokens/shadows';
import { typographyVars } from './tokens/typography';

/**
 * Generate CSS custom properties from theme tokens
 * @param {Object} themeTokens - resolved theme token object
 * @param {Object} options - mode and density metadata
 * @returns {Object} CSS custom properties as key-value pairs (use -- prefix in CSS)
 */
export const generateCSSVariables = (themeTokens = {}, options = {}) => {
  const semanticTokens = themeTokens.semantic || semantic;
  const spacingTokens = themeTokens.spacing || spacing;
  const controlHeightTokens = themeTokens.controlHeights || controlHeights;
  const radiusTokens = themeTokens.radius || radius;
  const shadowTokens = themeTokens.shadows || shadows;
  const densityTokens = themeTokens.density || {};
  const typographyTokens = themeTokens.typographyVars || typographyVars;
  const layoutTokens = themeTokens.layout || {};
  const activeMode = options.mode || themeTokens.mode || 'light';
  const activeDensity = options.density || densityTokens.current || 'default';
  const vars = {};

  // Brand colors
  vars['--color-primary'] = semanticTokens.interactive.primary;
  vars['--color-primary-hover'] = semanticTokens.interactive.primaryHover;
  vars['--color-primary-active'] = semanticTokens.interactive.primaryActive;
  vars['--color-primary-soft'] = semanticTokens.interactive.primarySoft;
  vars['--color-primary-foreground'] = semanticTokens.interactive.primaryForeground;
  vars['--color-primary-border'] = semanticTokens.interactive.primaryBorder;
  vars['--color-primary-muted'] = semanticTokens.interactive.primaryMuted;

  // Surfaces
  vars['--color-background'] = semanticTokens.surfaces.background;
  vars['--color-surface'] = semanticTokens.surfaces.surface;
  vars['--color-surface-subtle'] = semanticTokens.surfaces.surfaceSubtle;
  vars['--color-surface-muted'] = semanticTokens.surfaces.surfaceMuted;
  vars['--color-surface-elevated'] = semanticTokens.surfaces.surfaceElevated;

  // Text colors
  vars['--color-text-primary'] = semanticTokens.text.primary;
  vars['--color-text-secondary'] = semanticTokens.text.secondary;
  vars['--color-text-muted'] = semanticTokens.text.muted;
  vars['--color-text-inverse'] = semanticTokens.text.inverse;
  vars['--color-link'] = semanticTokens.interactive.primary;
  vars['--color-link-hover'] = semanticTokens.interactive.primaryHover;

  // Borders
  vars['--color-border'] = semanticTokens.borders.border;
  vars['--color-border-strong'] = semanticTokens.borders.borderStrong;
  vars['--color-focus-ring'] = semanticTokens.borders.focusRing;

  // Semantic states
  vars['--color-success'] = semanticTokens.states.success;
  vars['--color-success-soft'] = semanticTokens.states.successSoft;
  vars['--color-warning'] = semanticTokens.states.warning;
  vars['--color-warning-soft'] = semanticTokens.states.warningSoft;
  vars['--color-danger'] = semanticTokens.states.danger;
  vars['--color-danger-soft'] = semanticTokens.states.dangerSoft;
  vars['--color-info'] = semanticTokens.states.info;
  vars['--color-info-soft'] = semanticTokens.states.infoSoft;

  // Sidebar
  vars['--sidebar-background'] = semanticTokens.sidebar.background;
  vars['--sidebar-background-end'] = semanticTokens.sidebar.backgroundEnd;
  vars['--sidebar-foreground'] = semanticTokens.sidebar.foreground;
  vars['--sidebar-muted'] = semanticTokens.sidebar.muted;
  vars['--sidebar-border'] = semanticTokens.sidebar.border;
  vars['--sidebar-hover'] = semanticTokens.sidebar.hover;
  vars['--sidebar-active'] = semanticTokens.sidebar.active;
  vars['--sidebar-active-border'] = semanticTokens.sidebar.activeBorder;

  // Chat feature colors
  vars['--chat-primary'] = semanticTokens.chat.primary;
  vars['--chat-primary-light'] = semanticTokens.chat.primaryLight;
  vars['--chat-primary-dark'] = semanticTokens.chat.primaryDark;
  vars['--chat-primary-surface'] = semanticTokens.chat.primarySurface;
  vars['--chat-primary-border'] = semanticTokens.chat.primaryBorder;
  vars['--chat-success'] = semanticTokens.chat.success;
  vars['--chat-online'] = semanticTokens.chat.online;

  // Spacing
  Object.entries(spacingTokens).forEach(([key, value]) => {
    vars[`--space-${key}`] = `${value}px`;
  });

  // Control heights
  Object.entries(controlHeightTokens).forEach(([key, value]) => {
    vars[`--control-height-${key}`] = `${value}px`;
  });

  // Border radius
  Object.entries(radiusTokens).forEach(([key, value]) => {
    if (typeof value === 'number') {
      vars[`--radius-${key}`] = `${value}px`;
    } else {
      vars[`--radius-${key}`] = value;
    }
  });

  // Shadows
  Object.entries(shadowTokens).forEach(([key, value]) => {
    vars[`--shadow-${key}`] = value;
  });

  // Typography
  Object.entries(typographyTokens).forEach(([key, value]) => {
    vars[`--${key}`] = typeof value === 'number' ? `${value}px` : value;
  });

  // Density
  vars['--density-mode'] = activeDensity;
  Object.entries(densityTokens).forEach(([key, value]) => {
    if (key === 'current' || !value || typeof value !== 'object') {
      return;
    }
    Object.entries(value).forEach(([densityKey, densityValue]) => {
      const suffix = typeof densityValue === 'number' ? `${densityValue}px` : densityValue;
      vars[`--density-${key}-${densityKey}`] = suffix;
    });
  });
  const currentDensityTokens = densityTokens[activeDensity] || densityTokens.default || {};
  if (currentDensityTokens.controlHeight) {
    vars['--density-control-height'] = `${currentDensityTokens.controlHeight}px`;
  }
  if (currentDensityTokens.cardPadding) {
    vars['--density-card-padding'] = `${currentDensityTokens.cardPadding}px`;
  }
  if (currentDensityTokens.rowHeight) {
    vars['--density-row-height'] = `${currentDensityTokens.rowHeight}px`;
  }

  // Layout
  Object.entries(layoutTokens).forEach(([key, value]) => {
    vars[`--${key}`] = typeof value === 'number' ? `${value}px` : value;
  });
  vars['--theme-mode'] = activeMode;

  return vars;
};

/**
 * Inject CSS variables into the document
 * @param {Object} themeTokens - resolved theme token object
 * @param {HTMLElement} targetElement - Element to inject styles into (default: document.documentElement)
 * @param {Object} options - mode and density metadata
 */
export const injectCSSVariables = (themeTokens = {}, targetElement = null, options = {}) => {
  const target =
    targetElement || (typeof document !== 'undefined' ? document.documentElement : null);

  if (!target) {
    console.warn('injectCSSVariables: No target element available');
    return [];
  }

  const vars = generateCSSVariables(themeTokens, options);
  Object.entries(vars).forEach(([key, value]) => {
    target.style.setProperty(key, value);
  });
  return Object.keys(vars);
};

/**
 * Remove previously injected CSS variables from a scoped element.
 */
export const clearCSSVariables = (targetElement, variableKeys = []) => {
  if (!targetElement || !variableKeys.length) {
    return;
  }
  variableKeys.forEach((key) => {
    targetElement.style.removeProperty(key);
  });
};

export default generateCSSVariables;
